import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Container,
  Grid,
  Header,
  SegmentedControl,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { useSearchParams } from "react-router-dom";
import EventsVersionSelector from "./EventsVersionSelector";
import { OrderIdContext, StageContext } from "../../contexes/OrderIdContext";
import { useMarketplaceId } from "../../contexes/MarketplaceIdContext";
import useFetchDetails from "../../hooks/useFetchDetails";
import {
  getCbeLogGroups,
  getEventDateRange,
  getEventLogGroups,
} from "../../helpers/logUtils";
import {
  getAllProductIds,
  isBundleNonSplitTransaction,
  isDocumentBundleTransaction,
} from "../../helpers/bundleUtils";
import CbeVersionSelector from "../cbe/CbeVersionSelector";
import EventButtons from "../common/EventButtons";
import ErrorAlert from "../common/ErrorAlert";
import { useGlProduct } from "../../contexes/GlProductContext";
import { useFlowType } from "../../hooks/useFlowType";
import {
  FLOW_TYPES,
  FlowType,
  getFlowSelectorLabel,
} from "../../helpers/flowTypeUtils";
import {
  isAccountingEventsGroup,
  isCanonicalBusinessEvent,
  isDigitalBusinessDocument,
  isDigitalDocument,
} from "../../helpers/typeChecks";
import { CanonicalBusinessEvent } from "../../types/cbes";
import { AccountingEventsGroup } from "../../types/events";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../../types/documents";
import { MatchKeyType } from "../../types/matchSetState";

const getVersion = (
  data:
    | CanonicalBusinessEvent
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
): number => {
  if (isCanonicalBusinessEvent(data)) {
    return data.lockVersion;
  }

  if (isDigitalDocument(data)) {
    return data.version;
  }

  if (isAccountingEventsGroup(data)) {
    return data.sk;
  }

  throw Error("unsupported getVersion data", data);
};

export default function Results() {
  const orderId = useContext(OrderIdContext);
  const stage = useContext(StageContext);
  const { marketplaceId } = useMarketplaceId();
  const { glProduct } = useGlProduct();
  const [searchParams, setSearchParams] = useSearchParams();

  const [productsToggleConfig, setProductsToggleConfig] = useState<{
    show: boolean;
    onToggle: () => void;
  } | null>(null);

  const [versionViewConfig, setVersionViewConfig] = useState<{
    show: boolean;
    isTopLevel: boolean;
    onToggle: () => void;
  } | null>(null);

  const { data: detailsData } = useFetchDetails(stage, orderId);

  const eventData = detailsData?.events || [];
  const cbeData = detailsData?.cbes || [];

  const isEventNotEmpty = eventData.length > 0;
  const isCbeNotEmpty = cbeData.length > 0;

  const handleProductsToggle = useCallback((toggleFunction: () => void) => {
    setProductsToggleConfig({
      show: true,
      onToggle: toggleFunction,
    });
  }, []);

  const handleVersionViewToggle = useCallback(
    (toggleFunction: () => void, isTopLevel: boolean, shouldShow: boolean) => {
      setVersionViewConfig({
        show: shouldShow,
        isTopLevel,
        onToggle: toggleFunction,
      });
    },
    []
  );

  const {
    flowType,
    setFlowType,
    showFlowTypeSelector,
    filteredEvents,
    filteredCbes,
  } = useFlowType({
    eventData,
    cbeData,
    glProduct,
  });

  const { allProductIds, isDocumentBundle } = useMemo(() => {
    const documents = filteredEvents.filter(isDigitalBusinessDocument);

    if (documents.length === 0) {
      return { allProductIds: [], isDocumentBundle: false };
    }

    const isDocumentBundleDetected = isDocumentBundleTransaction(documents[0]);
    const productIds = Array.from(getAllProductIds(documents));
    const isBundleNonSplitDetected = isBundleNonSplitTransaction(documents);
    const hasMultipleProducts = productIds.length > 1;

    return {
      allProductIds: productIds,
      isDocumentBundle:
        hasMultipleProducts ||
        (isDocumentBundleDetected && !isBundleNonSplitDetected),
    };
  }, [filteredEvents]);

  const initialEventIndex = useMemo(() => {
    const urlVersion = searchParams.get("eventVersion");
    if (urlVersion !== null && filteredEvents.length > 0) {
      const parsed = parseInt(urlVersion, 10);
      if (!isNaN(parsed)) {
        const index = filteredEvents.findIndex((e) => getVersion(e) === parsed);
        if (index !== -1) return index;
      }
    }
    return undefined;
  }, [searchParams, filteredEvents]);

  const initialCbeIndex = useMemo(() => {
    const urlVersion = searchParams.get("cbeVersion");
    if (urlVersion !== null && filteredCbes.length > 0) {
      const parsed = parseInt(urlVersion, 10);
      if (!isNaN(parsed)) {
        const index = filteredCbes.findIndex((c) => c.lockVersion === parsed);
        if (index !== -1) return index;
      }
    }
    return undefined;
  }, [searchParams, filteredCbes]);

  const [selectedEventIndex, setSelectedEventIndex] = useState<number>(0);

  const [selectedCbeIndex, setSelectedCbeIndex] = useState<number>(0);

  const handleEventVersionChange = useCallback(
    (index: number) => {
      setSelectedEventIndex(index);
      const version = getVersion(filteredEvents[index]);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("eventVersion", version.toString());
        return newParams;
      });
    },
    [filteredEvents, setSearchParams]
  );

  const handleCbeVersionChange = useCallback(
    (index: number) => {
      setSelectedCbeIndex(index);
      const version = filteredCbes[index].lockVersion;
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("cbeVersion", version.toString());
        return newParams;
      });
    },
    [filteredCbes, setSearchParams]
  );

  const eventLogGroups = useMemo(() => {
    if (!isEventNotEmpty) return [];

    const dateRange = getEventDateRange(
      filteredEvents[selectedEventIndex] || eventData[selectedEventIndex],
      false
    );
    const startTime = dateRange[0];

    return getEventLogGroups(stage, filteredEvents, flowType, startTime);
  }, [
    isEventNotEmpty,
    stage,
    filteredEvents,
    flowType,
    selectedEventIndex,
    eventData,
  ]);

  const cbeLogGroups = useMemo(() => {
    return getCbeLogGroups(marketplaceId, stage);
  }, [marketplaceId, stage]);

  return (
    <Grid
      gridDefinition={[
        { colspan: { default: 12, s: 6 } },
        { colspan: { default: 12, s: 6 } },
      ]}
    >
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <EventButtons
                  identifier={orderId}
                  stage={stage}
                  logGroups={eventLogGroups}
                  dateRange={getEventDateRange(
                    filteredEvents[selectedEventIndex] ||
                      eventData[selectedEventIndex],
                    false
                  )}
                  data={filteredEvents}
                />

                {showFlowTypeSelector && (
                  <SegmentedControl
                    selectedId={flowType}
                    onChange={({ detail }) => {
                      setFlowType(detail.selectedId as FlowType);
                      setSearchParams((prev) => {
                        const newParams = new URLSearchParams(prev);
                        newParams.delete("cbeVersion");
                        newParams.delete("eventVersion");
                        return newParams;
                      });
                    }}
                    options={[
                      {
                        text: getFlowSelectorLabel(
                          FLOW_TYPES.ACCOUNTING,
                          eventData,
                          cbeData
                        ),
                        id: FLOW_TYPES.ACCOUNTING,
                      },
                      {
                        text: getFlowSelectorLabel(
                          FLOW_TYPES.DOCUMENT,
                          eventData,
                          cbeData
                        ),
                        id: FLOW_TYPES.DOCUMENT,
                      },
                    ]}
                  />
                )}
              </SpaceBetween>
            }
          >
            Events
          </Header>
        }
      >
        {isEventNotEmpty && filteredEvents.length > 0 ? (
          <EventsVersionSelector
            eventData={filteredEvents}
            cbeData={cbeData}
            onVersionChange={handleEventVersionChange}
            initialIndex={initialEventIndex}
          />
        ) : isEventNotEmpty ? (
          <ErrorAlert
            header={
              showFlowTypeSelector
                ? `No ${
                    flowType === FLOW_TYPES.DOCUMENT
                      ? MatchKeyType.ROYALTY
                      : MatchKeyType.REVENUE
                  } events found`
                : "No events found"
            }
            content={
              showFlowTypeSelector
                ? `Try changing the flow type filter to view ${
                    flowType === FLOW_TYPES.DOCUMENT
                      ? MatchKeyType.REVENUE
                      : MatchKeyType.ROYALTY
                  } events.`
                : ""
            }
          />
        ) : (
          <ErrorAlert header="Could not find any events" content="" />
        )}
      </Container>
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <EventButtons
                identifier={orderId}
                stage={stage}
                logGroups={cbeLogGroups}
                dateRange={getEventDateRange(
                  filteredCbes[selectedCbeIndex] || cbeData[selectedCbeIndex],
                  true
                )}
                data={filteredCbes}
                collapseProductsConfig={productsToggleConfig || undefined}
                versionViewConfig={versionViewConfig || undefined}
              />
            }
          >
            CBEs
          </Header>
        }
      >
        {isCbeNotEmpty && filteredCbes.length > 0 ? (
          <CbeVersionSelector
            cbeData={filteredCbes}
            onVersionChange={handleCbeVersionChange}
            allProductIds={allProductIds}
            showCbeProductBreakdown={isDocumentBundle}
            onProductsToggle={handleProductsToggle}
            onVersionViewToggle={handleVersionViewToggle}
            initialIndex={initialCbeIndex}
          />
        ) : isCbeNotEmpty ? (
          <ErrorAlert
            header={
              showFlowTypeSelector
                ? `No ${
                    flowType === FLOW_TYPES.DOCUMENT
                      ? MatchKeyType.ROYALTY
                      : MatchKeyType.REVENUE
                  } CBEs found`
                : "No CBEs found"
            }
            content={
              showFlowTypeSelector
                ? `Try changing the flow type filter to view ${
                    flowType === FLOW_TYPES.DOCUMENT
                      ? MatchKeyType.REVENUE
                      : MatchKeyType.ROYALTY
                  } CBEs.`
                : ""
            }
          />
        ) : (
          <ErrorAlert header="Could not find any CBEs" content="" />
        )}
      </Container>
    </Grid>
  );
}
